import type { CSSProperties } from 'react';
import { ICONOS, type FctNombreIcono } from '../iconos';
import { ISOTIPOS, COLORES_ISOTIPO, type FctForma } from '../isotipos';

/* ---------------------------------------------------------------------------
 * FctIcono — set del manual en una sola tinta (currentColor).
 * ------------------------------------------------------------------------- */
export interface FctIconoProps {
  nombre: FctNombreIcono;
  className?: string;
  style?: CSSProperties;
  /** Texto accesible. Si se omite, el icono es decorativo (aria-hidden). */
  titulo?: string;
}

export function FctIcono({ nombre, className, style, titulo }: FctIconoProps) {
  return (
    <svg
      className={['fct-icono', className].filter(Boolean).join(' ')}
      style={style}
      viewBox="0 0 24 24"
      role={titulo ? 'img' : undefined}
      aria-label={titulo}
      aria-hidden={titulo ? undefined : true}
      dangerouslySetInnerHTML={{ __html: ICONOS[nombre] }}
    />
  );
}

/* ---------------------------------------------------------------------------
 * FctIsotipo — seis círculos en la configuración de la herramienta.
 * Colores en orden de lectura (nadie elige el color). En una tinta para entornos.
 * ------------------------------------------------------------------------- */
export type FctTintaIsotipo = 'color' | 'una-tinta' | 'inverso';

export interface FctIsotipoProps {
  forma: FctForma;
  /** Alto en px. El ancho sale de la proporción de la forma. */
  alto?: number;
  /** color (herramienta) · una-tinta (#3A511F, entornos) · inverso (#E6EBE0, sobre oscuro). */
  tinta?: FctTintaIsotipo;
  titulo?: string;
}

const TINTA: Record<Exclude<FctTintaIsotipo, 'color'>, string> = { 'una-tinta': '#3A511F', inverso: '#E6EBE0' };

export function FctIsotipo({ forma, alto = 24, tinta = 'color', titulo }: FctIsotipoProps) {
  const puntos = ISOTIPOS[forma];
  const cols = Math.max(...puntos.map((p) => p[0])) + 1;
  const filas = Math.max(...puntos.map((p) => p[1])) + 1;
  const w = cols * 7.5;
  const h = filas * 7.5;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      height={alto}
      width={(alto * w) / h}
      role={titulo ? 'img' : undefined}
      aria-label={titulo}
      aria-hidden={titulo ? undefined : true}
      style={{ display: 'inline-block', flex: 'none' }}
    >
      {puntos.map(([c, r], i) => (
        <circle key={i} cx={c * 7.5 + 3.75} cy={r * 7.5 + 3.75} r={3.75} fill={tinta === 'color' ? COLORES_ISOTIPO[i] : TINTA[tinta]} />
      ))}
    </svg>
  );
}

/* ---------------------------------------------------------------------------
 * FctLogotipo — isotipo + nombre en Dissolve Light, siempre en minúscula.
 * Solo en cabecera de la herramienta y pantalla de acceso. En listas, Roboto.
 * ------------------------------------------------------------------------- */
export interface FctLogotipoProps {
  forma: FctForma;
  nombre: string;
  /** Cuerpo del nombre en px; el isotipo mide lo mismo de alto. */
  cuerpo?: number;
  sobreOscuro?: boolean;
  /** Entornos (GC.WebApps, GescomAux): isotipo en una tinta. */
  entorno?: boolean;
}

export function FctLogotipo({ forma, nombre, cuerpo = 28, sobreOscuro = false, entorno = false }: FctLogotipoProps) {
  const tinta: FctTintaIsotipo = entorno ? (sobreOscuro ? 'inverso' : 'una-tinta') : 'color';
  return (
    <span className={['fct-logotipo', sobreOscuro ? 'fct-sobre-oscuro' : ''].join(' ').trim()} style={{ fontSize: cuerpo }}>
      <FctIsotipo forma={forma} alto={cuerpo} tinta={tinta} />
      <span className="fct-logotipo__nombre">{nombre.toLocaleLowerCase('es')}</span>
    </span>
  );
}

/* ---------------------------------------------------------------------------
 * FctTrama — el isotipo a escala de página, recortado por un borde, casi invisible.
 * Una por pantalla, detrás de todo. Nunca detrás de una tabla o formulario.
 * ------------------------------------------------------------------------- */
export interface FctTramaProps {
  forma?: FctForma;
  ancho?: number;
  /** Tono de trama: #DCE3D2 sobre lienzo, #2E3E21 sobre marco. */
  color?: string;
  style?: CSSProperties;
}

export function FctTrama({ forma = 'gescom', ancho = 640, color = '#DCE3D2', style }: FctTramaProps) {
  const puntos = ISOTIPOS[forma];
  const cols = Math.max(...puntos.map((p) => p[0])) + 1;
  const filas = Math.max(...puntos.map((p) => p[1])) + 1;
  return (
    <div className="fct-trama" aria-hidden="true">
      <svg viewBox={`0 0 ${cols * 7.5} ${filas * 7.5}`} width={ancho} height={(ancho * filas) / cols} style={{ left: -ancho * 0.36, top: -ancho * 0.1, ...style }}>
        {puntos.map(([c, r], i) => (
          <circle key={i} cx={c * 7.5 + 3.75} cy={r * 7.5 + 3.75} r={3.75} fill={color} />
        ))}
      </svg>
    </div>
  );
}
